/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-unused-expressions */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { flatMap as _flatmap, flatMap as _flatMap } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import { PaymentConfirmationSelectors } from '@amfam/billing/paymentconfirmation/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingPaymentConfirmationModel,
  PaymentAccount,
  ScheduledPayment,
  SchedulePaymentPayloadModel
} from '@amfam/shared/models';
import { ArrayToList, PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import {
  ConfirmationConfig,
  ConfirmationStatus,
  DockingBarService,
  LoadingSpinnerService
} from '@amfam/ui-kit';

import { OneTimePayActions } from '../../..';
import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';
import {
  HelpText,
  NotModifiablePaymentErrorCodes,
  OneTimePaySetup,
  OTPAnalyticsModel
} from '../../+state/one-time-pay-feature.models';

@Component({
  selector: 'amfam-payment-confirmation-wrapper',
  templateUrl: './payment-confirmation-wrapper.component.html',
  styleUrls: ['./payment-confirmation-wrapper.component.scss'],
  standalone: false
})
export class PaymentConfirmationWrapperComponent implements OnInit, OnDestroy {
  errorItem: HelpText;
  reviewItems$: Observable<OneTimePaySetup[]>;
  config$: Observable<ConfirmationConfig>;
  submitStatus$: Observable<'Success' | 'Error' | 'Delete-Success' | 'Delete-Error'> =
    this.otpFacade.getScheduledPaymentOperationAndStatus();
  updatedStatus: string;
  immediatePayment$: Observable<'Immediate' | 'Future'> = this.otpFacade.getOTPDateType();
  successBodyText: string;
  paymentInfoObj: SchedulePaymentPayloadModel;
  isEditOTP: boolean;
  isNotModifiablePaymentErrorCode: boolean;
  paymentDateType: string;
  analyticsSend = false;

  private stop$ = new Subject<void>();

  constructor(
    private copyService: CopyService,
    private store: Store,
    private dockingBar: DockingBarService,
    private otpFacade: OneTimePayFeatureFacade,
    private spinnerService: LoadingSpinnerService,
    private utilService: UtilService,
    private feature: FeatureFlagService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.spinnerService.start();
    this.config$ = this.getConfig();
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.oneTimePayRefactor.dockingBarHeading')
    );
    combineLatest([
      this.store.select(PaymentConfirmationSelectors.selectPaymentConfirmationDataState),
      this.store.select(PaymentAccountSelectors.selectPaymentAccountEntities),
      this.store.select(BillAccountsSelectors.selectAllBillAccounts),
      this.submitStatus$,
      this.otpFacade.isEditOTP()
    ])
      .pipe(
        filter(([, paymentAccountEntities, ,]) => Object.keys(paymentAccountEntities)?.length > 0),
        withLatestFrom(this.otpFacade.getPaymentId(), this.otpFacade.getScheduledPaymentEntities()),
        takeUntil(this.stop$)
      )
      .subscribe(
        ([
          [paymentConfirmationObj, paymentAccountEntities, billAccounts, status, isEditOTP],
          paymentId,
          scheduledPaymentEntities
        ]) => {
          // adds missing docking bar on retry functions
          this.dockingBar.registerHeading(
            this.copyService.getCopy('billing.oneTimePayRefactor.dockingBarHeading')
          );
          paymentAccountEntities = paymentAccountEntities as Dictionary<PaymentAccount>;
          scheduledPaymentEntities = scheduledPaymentEntities as Dictionary<ScheduledPayment>;
          this.updatedStatus = status;
          this.isEditOTP = isEditOTP;
          const scheduledEntity = scheduledPaymentEntities[paymentId];
          // Build new object for payment method in case of credit card scheduled payment which can't be deleted and payment method has been deleted
          const schedulePaymentObj: PaymentAccount = {
            nickName: scheduledEntity?.paymentAccount.nickName,
            lastUpdateTimestamp: scheduledEntity?.lastUpdateTimestamp,
            // Since do not have information to show, passing empty strings.
            creditCard: {
              cardType: '',
              cardNumber: ''
            }
          };
          const updatedBillAccounts = paymentConfirmationObj.billAccounts
            ? paymentConfirmationObj.billAccounts
            : scheduledEntity?.billAccounts;
          const correlationId = this.utilService.generateId();
          this.paymentInfoObj = {
            payment: {
              receivedDate: paymentConfirmationObj.date
                ? paymentConfirmationObj.date
                : scheduledEntity?.receivedDate,
              totalPaymentAmount: Number(
                paymentConfirmationObj.amount
                  ? paymentConfirmationObj.amount
                  : scheduledEntity?.totalPaymentAmount
              ).toFixed(2),
              paymentAccount: {
                nickName: paymentConfirmationObj.paymentAccount
                  ? paymentConfirmationObj.paymentAccount
                  : scheduledEntity?.paymentAccount.nickName
              },
              billAccounts: updatedBillAccounts
            },
            paymentId: paymentConfirmationObj.paymentId
              ? paymentConfirmationObj.paymentId
              : paymentId,
            correlationId: correlationId
          };
          if (status === 'Success' || status === 'Error') {
            this.reviewItems$ = this.getReviewItems();
          } else {
            const pendingPaymentAccounts = billAccounts.filter(billAcc =>
              updatedBillAccounts?.find(acc => acc.billAccountNumber === billAcc.billAccountNumber)
            );
            const paymentObj = paymentConfirmationObj.paymentAccount
              ? paymentAccountEntities[paymentConfirmationObj.paymentAccount]
              : schedulePaymentObj;
            this.reviewItems$ = of(
              this.getDeleteItems(
                paymentConfirmationObj,
                paymentObj,
                scheduledEntity,
                pendingPaymentAccounts
              )
            );
          }
          if (status === 'Delete-Success') {
            this.errorItem = { text: 'Deleted', warning: true };
          }
          this.spinnerService.stop();
        }
      );
  }

  getDeleteItems(
    paymentConfirmationObj: BillingPaymentConfirmationModel,
    paymentObj: PaymentAccount,
    scheduledEntity: ScheduledPayment,
    pendingPaymentAccounts: BillAccount[]
  ) {
    return pendingPaymentAccounts.map(billAcc => ({
      billAccountNumber: `Acct ${new PrettyBillingAcctNum().transform(billAcc.billAccountNumber)}`,
      riskDescription: new ArrayToList().transform(
        _flatmap(billAcc.policyList, policy => policy.riskDescriptionList)
      ),
      paymentAmount: paymentConfirmationObj.amount
        ? paymentConfirmationObj.amount.toString()
        : scheduledEntity?.totalPaymentAmount.toString(),
      paymentDate: format(
        paymentConfirmationObj.date ? paymentConfirmationObj.date : scheduledEntity?.receivedDate,
        'MMM DD'
      ),
      policyTypeIcon: billAcc.icon,
      useNickNameAsTitle: true,
      paymentAccount: paymentObj
    }));
  }

  getReviewItems(): Observable<OneTimePaySetup[]> {
    return combineLatest([
      this.otpFacade.getBillAccountEntities(),
      this.otpFacade.getOTPEntities(),
      this.otpFacade.getPaymentAccountEntities()
    ]).pipe(
      take(1),
      map(([billAccountsDictionary, OTPSetupDictionary, paymentAccountDictionary]) => {
        const billAccountsForConfirmation = [];
        let totalPaymentAmount = 0;
        // eslint-disable-next-line guard-for-in
        for (const billAccountNumber in OTPSetupDictionary) {
          totalPaymentAmount =
            totalPaymentAmount + OTPSetupDictionary[billAccountNumber].selectedPaymentAmount;
        }
        // eslint-disable-next-line guard-for-in
        for (const billAccountNumber in OTPSetupDictionary) {
          billAccountsForConfirmation.push({
            billAccountNumber: `Acct ${new PrettyBillingAcctNum().transform(billAccountNumber)}`,
            riskDescription: new ArrayToList().transform(
              _flatMap(
                billAccountsDictionary[billAccountNumber].policyList,
                policy => policy.riskDescriptionList
              )
            ),
            policyTypeIcon: billAccountsDictionary[billAccountNumber].icon,
            paymentAmount: totalPaymentAmount.toString(),
            paymentDate: format(
              OTPSetupDictionary[billAccountNumber].selectedPaymentDate,
              'MMM DD'
            ),
            useNickNameAsTitle: true,
            paymentAccount:
              paymentAccountDictionary[OTPSetupDictionary[billAccountNumber].selectedPaymentAccount]
          });
        }
        return billAccountsForConfirmation;
      })
    );
  }

  getConfig(): Observable<ConfirmationConfig> {
    return combineLatest([
      this.otpFacade.getCustomerEmailAddress(),
      this.immediatePayment$,
      this.submitStatus$,
      this.otpFacade.getPaymentErrorCode(),
      this.otpFacade.getOTPReviewPaymentAmtAnalyticsInfo(),
      this.otpFacade.getOTPReviewPaymentDateAnalyticsInfo()
    ]).pipe(
      map(
        ([
          emailAddress,
          immediatePayment,
          submitStatus,
          paymentErrorCode,
          paymentAmount,
          paymentDate
        ]) => {
          this.paymentDateType = immediatePayment;
          let contextHeading;
          this.isNotModifiablePaymentErrorCode =
            NotModifiablePaymentErrorCodes.includes(paymentErrorCode);
          // Analytics check below
          if (submitStatus === 'Success') {
            this.sendAnalytics({
              pageAnalyticName: this.isEditOTP ? 'Edit:Complete' : 'Complete',
              pageAnalyticSubCategory3:
                immediatePayment === 'Immediate' ? 'Immediate Payment' : 'Scheduled Payment',
              paymentAmount: paymentAmount,
              paymentDate: paymentDate
            });
          } else if (submitStatus === 'Delete-Success') {
            this.sendAnalytics({
              pageAnalyticName: 'Edit:Deleted',
              pageAnalyticSubCategory3: 'Immediate Payment'
            });
          }
          // analytics for 'error' status
          if (submitStatus === 'Error') {
            if (this.isNotModifiablePaymentErrorCode) {
              const paymentAnalyticsError = {
                event: 'payment_error',
                payment_term: ''
              };
              this.analyticsFacade.trackEvent(paymentAnalyticsError);
              // analyitcs for specific error sub codes
              this.sendAnalytics({
                pageAnalyticName: this.feature.isEnabled('simplified_payment_p2')
                  ? 'Simplified:Error'
                  : 'Error',
                pageAnalyticSubCategory3: paymentErrorCode
              });
            } else {
              if (!this.isEditOTP) {
                const paymentErrorAnalytics = {
                  event: 'payment_error',
                  payment_term: ''
                };
                this.analyticsFacade.trackEvent(paymentErrorAnalytics);
              }
              this.sendAnalytics({
                pageAnalyticName: this.isEditOTP
                  ? 'Edit:Error'
                  : this.feature.isEnabled('simplified_payment_p2')
                    ? 'Simplified:Error'
                    : 'Error'
              });
            }
          }

          if (this.isNotModifiablePaymentErrorCode) {
            contextHeading = this.copyService.getCopy(
              `billing.paymentFailureVagueErrorMessages.heading.${paymentErrorCode}`
            );
          } else {
            if (this.isEditOTP) {
              contextHeading =
                submitStatus === 'Success'
                  ? this.copyService.getCopy(
                      `billing.oneTimePayRefactor.confirmationHeaderEditOTP-Success`
                    )
                  : this.copyService.getCopy(`billing.oneTimePayRefactor.confirmationHeaderError`);
            } else {
              contextHeading = this.copyService.getCopy(
                `billing.oneTimePayRefactor.confirmationHeader${submitStatus}${immediatePayment}`
              )
                ? this.copyService.getCopy(
                    `billing.oneTimePayRefactor.confirmationHeader${submitStatus}${immediatePayment}`
                  )
                : this.copyService.getCopy(
                    `billing.oneTimePayRefactor.confirmationHeader${submitStatus}`
                  );
            }
          }
          return {
            heading: contextHeading,
            status:
              submitStatus.indexOf('Success') !== -1 ||
              submitStatus.indexOf('Delete-Success') !== -1
                ? ConfirmationStatus.SUCCESS
                : ConfirmationStatus.ERROR,
            bodyText: this.isNotModifiablePaymentErrorCode
              ? this.copyService.getCopy(
                  `billing.paymentFailureVagueErrorMessages.body.${paymentErrorCode}`
                )
              : this.copyService.getCopy(
                  `billing.oneTimePayRefactor.confirmationBody${submitStatus}`,
                  { primaryEmail: emailAddress }
                ),
            subheading2: this.copyService.getCopy(
              `billing.oneTimePayRefactor.confirmationSubheading2${submitStatus}`
            ),
            ctaConfig: {
              primaryButtonName: this.isNotModifiablePaymentErrorCode
                ? 'Done'
                : this.copyService.getCopy(
                    `billing.oneTimePayRefactor.confirmationPrimaryBtn${submitStatus}`
                  ),
              tertiaryButtonName: this.isNotModifiablePaymentErrorCode
                ? null
                : this.copyService.getCopy(
                      `billing.oneTimePayRefactor.confirmationTertiaryBtn${submitStatus}`
                    )
                  ? this.copyService.getCopy(
                      `billing.oneTimePayRefactor.confirmationTertiaryBtn${submitStatus}`
                    )
                  : null
            }
          };
        }
      )
    );
  }

  primaryButtonClick() {
    if (this.isNotModifiablePaymentErrorCode) {
      this.store.dispatch(OneTimePayActions.goToBilling());
    } else {
      switch (this.updatedStatus) {
        case 'Success':
          this.store.dispatch(OneTimePayActions.goToBilling());
          break;
        case 'Error':
          this.spinnerService.start({ blockActions: true });
          this.isEditOTP ? this.otpFacade.editOTP() : this.otpFacade.submitOTP();
          this.analyticsSend = false;
          break;
        case 'Delete-Success':
          this.store.dispatch(OneTimePayActions.goToBilling());
          break;
        case 'Delete-Error':
          this.spinnerService.start({ blockActions: true });
          this.otpFacade.deletePayment(this.paymentInfoObj);
          this.analyticsSend = false;
          break;
      }
    }
  }

  done() {
    if (this.updatedStatus === 'Success') {
      const otpMakeAnotheranalyticType =
        (this.paymentDateType === 'Immediate' ? 'Immediate' : 'Scheduled') + ':MakeAnotherPayment';
      this.otpFacade.sendAnalytics({
        buttonAnalyticName: 'Complete:' + otpMakeAnotheranalyticType
      });
      this.store.dispatch(OneTimePayActions.goToMakeAnotherPayment());
    } else {
      this.store.dispatch(OneTimePayActions.goToBilling());
    }
  }

  sendAnalytics(analyticsData: OTPAnalyticsModel) {
    // send analytics only once for the page
    if (!this.analyticsSend) {
      this.otpFacade.sendAnalytics(analyticsData);
      this.analyticsSend = true;
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
